/**
 * UseSessionSnapshot
 *
 * Captures the initial play state when the editor opens and provides
 * a revert-to-snapshot capability. The revert is wrapped in a single
 * undo transaction so the entire revert is one Cmd+Z operation.
 */

import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ResolvedBlock } from '@/types/blocks'
import type { Player } from '@/types/Player.types'
import type { Drawing } from '@/types/Drawing.types'
import type { PlayBlockMutations } from './Types'
import { getResolvedBlockQueryKey } from '@/hooks/UseResolvedBlock'
import { playerBlocksToPlayers } from '@/adapters/PlayerAdapter'
import { drawingBlocksToDrawings } from '@/adapters/DrawingAdapter'

interface SessionSnapshot {
	players: Player[]
	drawings: Drawing[]
}

interface UseSessionSnapshotOptions {
	playBlockId: string
	resolvedBlock: ResolvedBlock | null
	mutations: PlayBlockMutations
	players: Player[]
	drawings: Drawing[]
	startTransaction: (label: string) => string
	endTransaction: (txId: string) => void
}

interface UseSessionSnapshotReturn {
	hasSnapshot: boolean
	revertToSnapshot: () => Promise<void>
}

export function useSessionSnapshot({
	playBlockId,
	resolvedBlock,
	mutations,
	players: currentPlayers,
	drawings: currentDrawings,
	startTransaction,
	endTransaction,
}: UseSessionSnapshotOptions): UseSessionSnapshotReturn {
	const queryClient = useQueryClient()
	const snapshotRef = useRef<SessionSnapshot | null>(null)
	const capturedRef = useRef(false)

	// Capture snapshot on first resolved block
	useEffect(() => {
		if (capturedRef.current || !resolvedBlock) return

		snapshotRef.current = {
			players: playerBlocksToPlayers(resolvedBlock.players.filter(p => p?.properties != null)),
			drawings: drawingBlocksToDrawings(resolvedBlock.drawings),
		}
		capturedRef.current = true
	}, [resolvedBlock])

	// Reset when play changes
	useEffect(() => {
		snapshotRef.current = null
		capturedRef.current = false
	}, [playBlockId])

	const revertToSnapshot = useCallback(async () => {
		const snapshot = snapshotRef.current
		if (!snapshot) return

		const txId = startTransaction('Revert Session')

		try {
			// Diff players: find added, deleted, modified
			const snapshotPlayerIds = new Set(snapshot.players.map(p => p.id))
			const currentPlayerIds = new Set(currentPlayers.map(p => p.id))

			// Delete players added since snapshot
			for (const player of currentPlayers) {
				if (!snapshotPlayerIds.has(player.id)) {
					await mutations.deletePlayer(player.id)
				}
			}

			// Restore players deleted since snapshot
			for (const player of snapshot.players) {
				if (!currentPlayerIds.has(player.id)) {
					await mutations.addPlayer(player)
				}
			}

			// Update modified players
			for (const snapshotPlayer of snapshot.players) {
				const current = currentPlayers.find(p => p.id === snapshotPlayer.id)
				if (!current) continue

				const changed =
					current.x !== snapshotPlayer.x ||
					current.y !== snapshotPlayer.y ||
					current.label !== snapshotPlayer.label ||
					current.color !== snapshotPlayer.color

				if (changed) {
					await mutations.updatePlayer(snapshotPlayer.id, {
						x: snapshotPlayer.x,
						y: snapshotPlayer.y,
						label: snapshotPlayer.label,
						color: snapshotPlayer.color,
					})
				}
			}

			// Diff drawings: find added, deleted, modified
			const snapshotDrawingIds = new Set(snapshot.drawings.map(d => d.id))
			const currentDrawingIds = new Set(currentDrawings.map(d => d.id))

			// Delete drawings added since snapshot
			for (const drawing of currentDrawings) {
				if (!snapshotDrawingIds.has(drawing.id)) {
					await mutations.deleteDrawing(drawing.id)
				}
			}

			// Restore drawings deleted since snapshot
			for (const drawing of snapshot.drawings) {
				if (!currentDrawingIds.has(drawing.id)) {
					await mutations.addDrawing(drawing)
				}
			}

			// Update modified drawings (compare by serialization since nested objects)
			for (const snapshotDrawing of snapshot.drawings) {
				const current = currentDrawings.find(d => d.id === snapshotDrawing.id)
				if (!current) continue

				const changed =
					JSON.stringify(current.points) !== JSON.stringify(snapshotDrawing.points) ||
					JSON.stringify(current.style) !== JSON.stringify(snapshotDrawing.style) ||
					JSON.stringify(current.segments) !== JSON.stringify(snapshotDrawing.segments)

				if (changed) {
					await mutations.updateDrawing(snapshotDrawing.id, {
						points: snapshotDrawing.points,
						style: snapshotDrawing.style,
						segments: snapshotDrawing.segments,
					})
				}
			}
		} finally {
			endTransaction(txId)
		}

		// Invalidate cache to refresh UI
		queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
	}, [
		currentPlayers,
		currentDrawings,
		mutations,
		playBlockId,
		queryClient,
		startTransaction,
		endTransaction,
	])

	return {
		hasSnapshot: capturedRef.current,
		revertToSnapshot,
	}
}
